import React from "react";
import {
  Check,
  Filter,
  GripVertical,
  Keyboard,
  ListTodo,
  Moon,
  Paperclip,
  Search,
  Type,
} from "lucide-react";
import AnimateIn from "../ui/AnimateIn";
import BoardMockup from "./mockups/BoardMockup";
import CalendarMockup from "./mockups/CalendarMockup";
import PersistentTaskMockup from "./mockups/PersistentTaskMockup";
import SmartInputMockup from "./mockups/SmartInputMockup";
import VoiceMockup from "./mockups/VoiceMockup";
import WorkingHoursMockup from "./mockups/WorkingHoursMockup";
import SharedListMockup from "./mockups/SharedListMockup";

/* ─── Feature Section ─── */

function FeatureSection({
  title,
  description,
  bullets,
  mockup,
  reversed = false,
  id,
}: {
  title: string;
  description: string;
  bullets: string[];
  mockup: React.ReactNode;
  reversed?: boolean;
  id?: string;
}) {
  return (
    <section id={id} className="py-20 md:py-28">
      <div
        className={`max-w-6xl mx-auto px-6 flex flex-col ${
          reversed ? "md:flex-row-reverse" : "md:flex-row"
        } items-center gap-12 md:gap-16`}
      >
        <AnimateIn className="md:w-1/2">
          <h2 className="font-display text-3xl md:text-4xl text-warm-text-primary mb-4 leading-tight">
            {title}
          </h2>
          <p className="text-warm-text-secondary text-lg leading-relaxed mb-6">{description}</p>
          <ul className="space-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-warm-text-secondary">
                <Check className="w-5 h-5 text-warm-blue shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </AnimateIn>
        <AnimateIn className="md:w-1/2" delay={200}>
          {mockup}
        </AnimateIn>
      </div>
    </section>
  );
}

/* ─── Secondary Feature Card ─── */

function FeatureCard({
  icon,
  title,
  description,
  delay = 0,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}) {
  return (
    <AnimateIn delay={delay}>
      <div className="bg-warm-bg-card border border-warm-border rounded-card p-5 hover:shadow-soft-lg transition-all duration-300 hover:-translate-y-1 h-full animate-fade-in-up">
        <div className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-warm-blue/10 text-warm-blue mb-3">
          {icon}
        </div>
        <h3 className="font-display text-sm text-warm-text-primary mb-1.5">
          {title}
        </h3>
        <p className="text-xs text-warm-text-secondary leading-relaxed">{description}</p>
      </div>
    </AnimateIn>
  );
}

/* ─── The smaller feature tiles ─── */

const SECONDARY_FEATURES = [
  {
    icon: <ListTodo className="w-5 h-5" />,
    title: "Rich Subtasks",
    description: "Break any task into subtasks with their own priorities, dates, tags, and attachments.",
  },
  {
    icon: <Paperclip className="w-5 h-5" />,
    title: "File Attachments",
    description: "Attach documents, images, and PDFs directly to tasks. Up to 10 files per task.",
  },
  {
    icon: <Filter className="w-5 h-5" />,
    title: "Custom Filters",
    description: "Save complex filter combinations and switch between views with one click.",
  },
  {
    icon: <Keyboard className="w-5 h-5" />,
    title: "Keyboard Shortcuts",
    description: "Navigate your entire workflow without touching the mouse. Press ? to see all shortcuts.",
  },
  {
    icon: <Search className="w-5 h-5" />,
    title: "Instant Search",
    description: "Find any task instantly across titles, descriptions, and subtasks.",
  },
  {
    icon: <Moon className="w-5 h-5" />,
    title: "Dark Mode",
    description: "Light and dark themes that follow your system, or the one you pick.",
  },
  {
    icon: <GripVertical className="w-5 h-5" />,
    title: "Drag & Drop",
    description: "Reorder tasks, lists, and calendar events with intuitive drag and drop.",
  },
  {
    icon: <Type className="w-5 h-5" />,
    title: "Rich Text Editor",
    description: "Format descriptions with bold, italic, lists, and embedded images.",
  },
];

export default function FeaturesSection() {
  return (
    <>
    {/* ─── Features Intro ─── */}
    <section id="features" className="py-10">
      <AnimateIn>
        <div className="text-center max-w-2xl mx-auto px-6">
          <h2 className="font-display text-3xl md:text-4xl text-warm-text-primary mb-4">
            Everything you need to stay organized
          </h2>
          <p className="text-warm-text-secondary text-lg">
            Powerful features designed to make task management fast, intuitive, and even enjoyable.
          </p>
        </div>
      </AnimateIn>
    </section>

    {/* ─── Feature: Panoramic Board View ─── */}
    <FeatureSection
      title="See it all. At a glance."
      description="Unlike task apps that hide your work behind clicks and menus, JuDI's panoramic board shows every list, task, and subtask in one sweeping view."
      bullets={[
        "Organize tasks into lists with custom emoji headers",
        "See priorities, deadlines, and subtask progress at a glance",
        "Drag tasks to reorder them; rearrange lists from Reorder on the board",
      ]}
      mockup={<BoardMockup />}
    />

    {/* ─── Feature: Smart Input ─── */}
    <section className="bg-warm-bg-elevated">
      <FeatureSection
        reversed
        title="Type naturally. We'll figure out the rest."
        description="Write 'Meeting with John tomorrow at 2pm' and JuDI automatically extracts the date, time, and cleans up the title. No more clicking through date pickers."
        bullets={[
          "Understands 'today', 'tomorrow', day names, 'next week', and more",
          "Parses time formats like 'at 2pm' or 'at 14:30'",
          "Extracts duration from '1h 30m' format",
        ]}
        mockup={<SmartInputMockup />}
      />
    </section>

    {/* ─── Feature: AI Voice Input ─── */}
    <FeatureSection
      title="Speak it. Done."
      description="Just say what's on your mind. JuDI's AI transcribes your speech and automatically creates structured tasks — complete with priorities, dates, and lists."
      bullets={[
        "Press V to start recording from anywhere in the app",
        "AI extracts dates, priorities, lists, and subtasks",
        "Review and edit proposed tasks before saving",
      ]}
      mockup={<VoiceMockup />}
    />

    {/* ─── Feature: Google Calendar ─── */}
    <section className="bg-warm-bg-elevated">
      <FeatureSection
        reversed
        title="Your calendar and tasks, finally in sync."
        description="Bi-directional sync with Google Calendar means your tasks appear on your calendar, and your events appear in JuDI. Stop switching between apps."
        bullets={[
          "Drag tasks onto the calendar to schedule them instantly",
          "See Google Calendar events alongside your JuDI tasks",
          "Toggle visibility of individual calendars",
        ]}
        mockup={<CalendarMockup />}
      />
    </section>

    {/* ─── Feature: Carry over ─── */}
    <FeatureSection
      title="Never let a task slip through the cracks."
      description="Some tasks aren't one-and-done — they stick with you until you finish them. Turn on Carry over and JuDI rolls unfinished tasks and sub-tasks forward automatically, so they're waiting for you tomorrow instead of buried in yesterday."
      bullets={[
        "Turn on Carry over on any scheduled task or sub-task",
        "Unfinished tasks auto-reschedule to the next day — no manual drag needed",
        "Pinned events update on Google Calendar too, keeping everything in sync",
      ]}
      mockup={<PersistentTaskMockup />}
    />

    {/* ─── Feature: Working hours only ─── */}
    <FeatureSection
      reversed
      title="Some tasks only belong on a weekday."
      description="Call a vendor, join a standup, file the expense report — work tasks have no business hiding on a Saturday calendar. Flip on Working hours only and JuDI quietly keeps them between Monday and Friday, 9 to 5. The picker won't even offer a weekend, and unfinished work-hours tasks skip Saturday and Sunday entirely to land back on Monday."
      bullets={[
        "Weekends and out-of-hours slots disappear from the picker — invalid choices aren't even offered",
        "Friday's loose ends jump straight to Monday morning, not Saturday",
        "Filter the board to only work-hours tasks on Monday, or hide them on Saturday for a real weekend",
      ]}
      mockup={<WorkingHoursMockup />}
    />

    {/* ─── Feature: Shared lists ─── */}
    <section className="bg-warm-bg-elevated">
      <FeatureSection
        title="Some work isn't just yours."
        description="Share a list with anyone who already uses JuDI, and everything in it — tasks, subtasks, notes, files — becomes something you're both looking at, not two copies to keep in sync."
        bullets={[
          "Invite by email; they see it the next time they open JuDI",
          "Everyone can add, edit, and complete tasks — only the owner can rename or delete the list",
          "Every task shows who added it, so nothing is a mystery",
        ]}
        mockup={<SharedListMockup />}
      />
    </section>

    {/* ─── Secondary Features Grid ─── */}
    <section className="py-20 md:py-28 bg-warm-bg-elevated">
      <div className="max-w-6xl mx-auto px-6">
        <AnimateIn>
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-warm-text-primary mb-4">
              And so much more
            </h2>
            <p className="text-warm-text-secondary text-lg max-w-xl mx-auto">
              Every detail has been thoughtfully designed to keep you productive.
            </p>
          </div>
        </AnimateIn>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SECONDARY_FEATURES.map((f, i) => (
            <FeatureCard
              key={f.title}
              icon={f.icon}
              title={f.title}
              description={f.description}
              delay={i * 75}
            />
          ))}
        </div>
      </div>
    </section>
    </>
  );
}
